import { useCallback, useEffect, useRef, useState } from "react";
import { createSearchParams, useNavigate } from "react-router-dom";
import { Search, X } from "lucide-react";
import PageRoutes from "@/utils/constants";
import { useAuthState } from "@/v2/hooks/useAuthState";
import { SearchResultsPanel } from "./SearchResultsPanel";
import { useDebouncedScheduleSearch } from "./useDebouncedScheduleSearch";

type HeaderSearchProps = {
  customer: string;
};

const LIMIT = 5;

export function DesktopHeaderSearch({ customer }: HeaderSearchProps) {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuthState();
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const {
    filters,
    setSearchText,
    resetSearch,
    toggleFilter,
    selectedTeams,
    selectedChannels,
    liveQuery,
    vodQuery,
    channelsQuery,
    favoritesQuery,
  } = useDebouncedScheduleSearch({ customer, isAuthenticated, enabled: isOpen, limit: LIMIT });

  const closeDropdown = useCallback(() => {
    setIsOpen(false);
    inputRef.current?.blur();
  }, []);

  const handleResetSearch = useCallback(() => {
    resetSearch();
    inputRef.current?.focus();
  }, [resetSearch]);

  useEffect(() => {
    if (!isOpen) return;
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        closeDropdown();
      }
    }
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") {
        closeDropdown();
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, closeDropdown]);

  const handleSeeAll = useCallback(
    (route: string) => {
      const params = createSearchParams({
        search: filters.search || "",
        team: filters.team || "",
        channel_id: filters.channel || "",
      });
      navigate({ pathname: route, search: `?${params.toString()}` });
      closeDropdown();
    },
    [filters, navigate, closeDropdown]
  );

  const handleManageFavorites = useCallback(() => {
    navigate(PageRoutes.AccountSettings);
    closeDropdown();
  }, [navigate, closeDropdown]);

  return (
    <div ref={containerRef} className="relative hidden h-9 w-[180px] min-[1200px]:block">
      {/* Fixed-footprint wrapper: the visible box below is absolutely
          positioned and anchored to the right edge, so growing it on open
          overlays the nav to its left instead of reflowing the row (the
          bell/avatar to the right never move). */}
      <div
        className={`absolute right-0 top-0 z-20 flex h-9 items-center rounded-md border border-input bg-background px-3 shadow-xs transition-all duration-300 ease-in-out focus-within:border-ring ${
          isOpen ? "w-[min(90vw,350px)] lg:w-[min(45vw,450px)]" : "w-[180px]"
        }`}
      >
        <Search className="mr-2 size-4 shrink-0 text-muted-foreground" />
        <input
          ref={inputRef}
          type="text"
          placeholder="Search..."
          aria-label="Search"
          autoComplete="off"
          value={filters.search}
          onChange={(e) => {
            setSearchText(e.target.value);
            setIsOpen(true);
          }}
          onFocus={() => setIsOpen(true)}
          onKeyDown={(e) => {
            if (e.key === "Escape") closeDropdown();
          }}
          className="v2-search-input w-full border-none bg-transparent p-0 text-sm outline-none placeholder:text-muted-foreground focus:outline-none focus:ring-0 focus-visible:outline-none"
        />
        {filters.search ? (
          <button
            type="button"
            onClick={handleResetSearch}
            className="absolute right-2 flex size-6 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
            aria-label="Clear search"
          >
            <X className="size-4" />
          </button>
        ) : null}
      </div>

      {isOpen && (
        <div className="v2 absolute right-0 top-full z-50 mt-1.5 w-[min(1100px,calc(100vw_-_2rem))] rounded-lg border border-border bg-popover shadow-lg">
          <div className="max-h-[550px] overflow-y-auto overflow-x-hidden">
            <SearchResultsPanel
              idPrefix="desktop"
              layout="desktop"
              filters={filters}
              selectedTeams={selectedTeams}
              selectedChannels={selectedChannels}
              toggleFilter={toggleFilter}
              liveQuery={liveQuery}
              vodQuery={vodQuery}
              channelsQuery={channelsQuery}
              favoritesQuery={favoritesQuery}
              isAuthenticated={isAuthenticated}
              limit={LIMIT}
              onSeeAll={handleSeeAll}
              onManageFavorites={handleManageFavorites}
            />
          </div>
        </div>
      )}
    </div>
  );
}
